// Measuring tools (Year 3 pictures, 21 Oct 2026): a ruler with a strip lying
// along it, a measuring jug with water in it, and a kitchen scale with a
// needle. Like the thermometer, the length, the water level and the reading
// are drawn against the marks and never written or spoken: reading the tool
// is the question.
//
// Pure parsing and geometry only, no components (see
// practice-diagram-data.ts).

export type RulerSpec = {
  type: 'ruler';
  unit: 'cm';
  // Whole centimetres, from 0.
  length: number;
  // Where the strip starts and ends, on a centimetre or half way.
  objectFrom: number;
  objectTo: number;
};

export type JugSpec = {
  type: 'jug';
  unit: 'mL' | 'L';
  max: number;
  // The gap between one mark and the next.
  step: number;
  // Every how many marks carry their number, counting from 0.
  labelEvery: number;
  level: number;
};

export type ScaleSpec = {
  type: 'scale';
  unit: 'kg' | 'g';
  max: number;
  step: number;
  labelEvery: number;
  reading: number;
};

export type MeasureSpec = RulerSpec | JugSpec | ScaleSpec;

// More marks or numbers than this cannot be told apart on a phone.
const MAX_STEPS = 40;
const MAX_NUMBERS = 11;

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isWhole(value: number): boolean {
  return Math.abs(value - Math.round(value)) < 1e-9;
}

// The smallest gap between numbered marks that keeps to ten gaps, and that
// fits the scale exactly so its top mark always has its number.
function defaultLabelEvery(steps: number): number {
  for (let every = 1; every <= steps; every += 1) {
    if (steps % every === 0 && steps / every <= 10) return every;
  }
  return steps;
}

// The marks of a jug or a scale, from 0 to max, and the value the tool
// shows, which must sit on a mark or half way between two.
function readMarks(
  record: Record<string, unknown>,
  shown: unknown,
): { max: number; step: number; labelEvery: number; value: number } | null {
  const { max, step } = record;
  if (!isNumber(max) || !isNumber(step) || !isNumber(shown)) return null;
  if (max <= 0 || step <= 0) return null;
  if (!isWhole(max / step)) return null;
  const steps = Math.round(max / step);
  if (steps < 1 || steps > MAX_STEPS) return null;
  const given = record.labelEvery;
  let labelEvery: number;
  if (given === undefined) {
    labelEvery = defaultLabelEvery(steps);
  } else {
    if (!isNumber(given) || !Number.isInteger(given) || given < 1) return null;
    labelEvery = given;
  }
  if (Math.floor(steps / labelEvery) + 1 > MAX_NUMBERS) return null;
  if (shown < 0 || shown > max) return null;
  if (!isWhole(shown / (step / 2))) return null;
  return { max, step, labelEvery, value: shown };
}

export function readRuler(record: Record<string, unknown>): RulerSpec | null {
  const { unit, length, objectFrom, objectTo } = record;
  if (unit !== 'cm') return null;
  if (!isNumber(length) || !Number.isInteger(length)) return null;
  // A Year 3 ruler: longer ones make the numbers too small on a phone.
  if (length < 5 || length > 20) return null;
  if (!isNumber(objectFrom) || !isNumber(objectTo)) return null;
  if (!isWhole(objectFrom * 2) || !isWhole(objectTo * 2)) return null;
  if (objectFrom < 0 || objectTo > length || objectFrom >= objectTo) {
    return null;
  }
  return { type: 'ruler', unit, length, objectFrom, objectTo };
}

export function readJug(record: Record<string, unknown>): JugSpec | null {
  const { unit } = record;
  if (unit !== 'mL' && unit !== 'L') return null;
  const marks = readMarks(record, record.level);
  if (marks === null) return null;
  const { max, step, labelEvery, value } = marks;
  return { type: 'jug', unit, max, step, labelEvery, level: value };
}

export function readScale(record: Record<string, unknown>): ScaleSpec | null {
  const { unit } = record;
  if (unit !== 'kg' && unit !== 'g') return null;
  const marks = readMarks(record, record.reading);
  if (marks === null) return null;
  const { max, step, labelEvery, value } = marks;
  return { type: 'scale', unit, max, step, labelEvery, reading: value };
}

function places(value: number): number {
  return (String(value).split('.')[1] ?? '').length;
}

// A number on the scale, free of the stray digits that adding decimals
// leaves behind.
function tidy(step: number, max: number, value: number): number {
  return Number(value.toFixed(Math.max(places(step), places(max))));
}

const UNIT_WORDS: Record<string, [string, string]> = {
  mL: ['millilitre', 'millilitres'],
  L: ['litre', 'litres'],
  kg: ['kilogram', 'kilograms'],
  g: ['gram', 'grams'],
};

function unitWord(unit: string, amount: number): string {
  const [one, many] = UNIT_WORDS[unit] ?? [unit, unit];
  return amount === 1 ? one : many;
}

// What a screen reader is told in place of each tool: its scale, and that
// something is being measured. Never the length, level or reading.
export function describeRuler(ruler: RulerSpec): string {
  return `A ruler marked in centimetres from 0 to ${String(ruler.length)}, with a strip lying along it.`;
}

export function describeJug(jug: JugSpec): string {
  return `A measuring jug marked from 0 to ${String(jug.max)} ${unitWord(jug.unit, jug.max)}, with water in it.`;
}

export function describeScale(scale: ScaleSpec): string {
  return `A kitchen scale marked from 0 to ${String(scale.max)} ${unitWord(scale.unit, scale.max)}. The needle shows how heavy it is.`;
}

// What follows is where everything is drawn. The units are close to one
// pixel each when the picture is shown at its natural width.

export const MEASURE_LABEL_SIZE = 13;
const DIGIT_WIDTH = 7.5;

export type RulerTick = {
  value: number;
  x: number;
  // The number under a whole centimetre; null for a half-centimetre mark.
  label: string | null;
};

export type RulerPicture = {
  ticks: RulerTick[];
  body: { x: number; y: number; width: number; height: number };
  // The strip lies above the ruler, from one x to the other.
  strip: { from: number; to: number; y: number; height: number };
  width: number;
  height: number;
};

const CENTIMETRE = 24;
const RULER_LEFT = 20;
const STRIP_TOP = 10;
const STRIP_HEIGHT = 14;
const RULER_TOP = 44;
const RULER_HEIGHT = 46;

export function rulerPicture(ruler: RulerSpec): RulerPicture {
  const xOf = (centimetres: number) => RULER_LEFT + centimetres * CENTIMETRE;
  const ticks: RulerTick[] = [];
  for (let half = 0; half <= ruler.length * 2; half += 1) {
    const value = half / 2;
    ticks.push({
      value,
      x: xOf(value),
      label: half % 2 === 0 ? String(value) : null,
    });
  }
  const body = {
    x: RULER_LEFT - 12,
    y: RULER_TOP,
    width: ruler.length * CENTIMETRE + 24,
    height: RULER_HEIGHT,
  };
  return {
    ticks,
    body,
    strip: {
      from: xOf(ruler.objectFrom),
      to: xOf(ruler.objectTo),
      y: STRIP_TOP,
      height: STRIP_HEIGHT,
    },
    width: body.x + body.width + 8,
    height: RULER_TOP + RULER_HEIGHT + 6,
  };
}

export type ScaleTick = {
  value: number;
  // The number beside the mark; null for a mark with no number.
  label: string | null;
};

export type JugTick = ScaleTick & { y: number };

export type JugPicture = {
  ticks: JugTick[];
  // The inside walls and the base of the jug, and its open top.
  left: number;
  right: number;
  top: number;
  bottom: number;
  // Where the top of the water sits.
  waterTop: number;
  tickFrom: number;
  labelX: number;
  width: number;
  height: number;
};

const JUG_LEFT = 46;
const JUG_RIGHT = 150;
const JUG_TOP = 24;

function label(
  index: number,
  labelEvery: number,
  value: number,
): string | null {
  return index % labelEvery === 0 ? String(value) : null;
}

export function jugPicture(jug: JugSpec): JugPicture {
  const { max, step, labelEvery } = jug;
  const steps = Math.round(max / step);
  // Marks sit close together when few of them carry a number, and further
  // apart when every one does, so two numbers never touch.
  const gap = Math.max(8, Math.min(16, Math.ceil(18 / labelEvery)));
  const bottom = JUG_TOP + 18 + steps * gap;
  const yOf = (value: number) => bottom - (value / step) * gap;
  const ticks: JugTick[] = [];
  for (let index = 0; index <= steps; index += 1) {
    const value = tidy(step, max, index * step);
    ticks.push({
      value,
      y: yOf(value),
      label: label(index, labelEvery, value),
    });
  }
  const labelX = JUG_RIGHT + 10;
  const widest = Math.max(
    ...ticks.map((tick) => (tick.label ?? '').length * DIGIT_WIDTH),
  );
  return {
    ticks,
    left: JUG_LEFT,
    right: JUG_RIGHT,
    top: JUG_TOP,
    bottom,
    waterTop: yOf(jug.level),
    tickFrom: JUG_RIGHT - 16,
    labelX,
    width: labelX + widest + 10,
    height: bottom + 10,
  };
}

export type DialTick = ScaleTick & {
  // Turned clockwise from straight up, in degrees.
  angle: number;
  outer: { x: number; y: number };
  inner: { x: number; y: number };
  labelAt: { x: number; y: number };
};

export type ScalePicture = {
  ticks: DialTick[];
  centre: { x: number; y: number };
  radius: number;
  needle: { angle: number; tip: { x: number; y: number } };
  // The pan the food sits on, above the dial.
  pan: { x: number; y: number; width: number; height: number };
  width: number;
  height: number;
};

const DIAL_RADIUS = 84;
const DIAL_X = 120;
const DIAL_Y = 150;
// 0 sits at the bottom left and the top of the scale at the bottom right,
// with the gap between them at the bottom.
const SWEEP = 300;

function pointAt(angle: number, distance: number): { x: number; y: number } {
  const turn = (angle * Math.PI) / 180;
  return {
    x: Number((DIAL_X + distance * Math.sin(turn)).toFixed(2)),
    y: Number((DIAL_Y - distance * Math.cos(turn)).toFixed(2)),
  };
}

export function scaleAngle(scale: ScaleSpec, value: number): number {
  return Number((-SWEEP / 2 + (SWEEP * value) / scale.max).toFixed(4));
}

export function scalePicture(scale: ScaleSpec): ScalePicture {
  const { max, step, labelEvery } = scale;
  const steps = Math.round(max / step);
  const ticks: DialTick[] = [];
  for (let index = 0; index <= steps; index += 1) {
    const value = tidy(step, max, index * step);
    const angle = scaleAngle(scale, value);
    const tickLabel = label(index, labelEvery, value);
    ticks.push({
      value,
      label: tickLabel,
      angle,
      outer: pointAt(angle, DIAL_RADIUS - 6),
      inner: pointAt(angle, DIAL_RADIUS - (tickLabel === null ? 13 : 20)),
      labelAt: pointAt(angle, DIAL_RADIUS - 34),
    });
  }
  const needleAngle = scaleAngle(scale, scale.reading);
  return {
    ticks,
    centre: { x: DIAL_X, y: DIAL_Y },
    radius: DIAL_RADIUS,
    needle: { angle: needleAngle, tip: pointAt(needleAngle, DIAL_RADIUS - 18) },
    pan: { x: DIAL_X - 72, y: 12, width: 144, height: 14 },
    width: DIAL_X * 2,
    height: DIAL_Y + DIAL_RADIUS + 22,
  };
}
